import { config } from '@config'
import { OrderType } from '@modules/common/models/enums/OrderManagement'
import { HttpService } from '@services/HttpService'
import { storage } from '@services/storageService'
import { nav } from '@stores/nav'
import { productPriceStore } from '@stores'
import { ORDER_SERVICE_ENDPOINTS } from '../constants/apiConstants'
import OrderDataService from './OrderDataService'

const mapProductClientDraftQuantity = products => {
  const {
    queryParams: { orderId }
  } = nav
  return products.map(product => ({
    ...product,
    ...(product.summary
      ? {
          summary: {
            ...product.summary,
            orderProduct: {
              ...product.summary.orderProduct,
              totalByClientOrderQuantity: (
                storage.getItem(`${orderId}--${product.id}`) as any
              )?.draftQuantity,
              totalByClientOrderValue:
                (storage.getItem(`${orderId}--${product.id}`) as any)?.draftQuantity *
                  productPriceStore.getPriceForProduct(product.id)
                    ?.wholesaleProductPrice || 0
            }
          },
          quantity: {
            avgDepth: product?.summary?.orderProduct?.avgDepth,
            doorProducts: product?.summary?.doorProducts,
            gradingAttributes: product?.summary?.orderProduct?.gradingAttributes,
            inactiveSizes: product?.summary?.orderProduct?.inactiveSizes || [],
            totalByDraftQuantity: product?.summary?.orderProduct?.totalByDraftQuantity,
            totalByProductQuantity:
              product?.summary?.orderProduct?.totalByProductQuantity,
            totalBySizeQuantity: product?.summary?.orderProduct?.totalBySizeQuantity,
            totalDraftValueWithCurrency:
              product?.summary?.orderProduct?.totalDraftValueWithCurrency,
            totalProductValueWithCurrency:
              product?.summary?.orderProduct?.totalProductValueWithCurrency,
            totalSizedValueWithCurrency:
              product?.summary?.orderProduct?.totalSizedValueWithCurrency,
            totalValueByDraftQuantity:
              product?.summary?.orderProduct?.totalValueByDraftQuantity,
            totalValueByProductQuantity:
              product?.summary?.orderProduct?.totalValueByProductQuantity,
            totalValueBySizeQuantity:
              product?.summary?.orderProduct?.totalValueBySizeQuantity
          }
        }
      : {}),
    ...(product.quantity
      ? {
          quantity: {
            ...product.quantity,
            totalByClientOrderQuantity: (
              storage.getItem(`${orderId}--${product.id}`) as any
            )?.draftQuantity,
            totalByClientOrderValue:
              (storage.getItem(`${orderId}--${product.id}`) as any)?.draftQuantity *
                productPriceStore.getPriceForProduct(product.id)?.wholesaleProductPrice ||
              0
          }
        }
      : {})
  }))
}

const mapHierarchyToClientDraftQuantity = (heirarchy = [], orderTotalKpis = {}) => {
  const {
    queryParams: { orderId }
  } = nav
  return heirarchy.map((heirarchyObj, index) => ({
    ...heirarchyObj,
    ...(heirarchyObj.children?.length
      ? {
          children: mapHierarchyToClientDraftQuantity(heirarchyObj.children)
        }
      : {}),
    kpis: {
      ...heirarchyObj.kpis,
      totalByClientOrderQuantity: (
        storage.getItem(`${orderId} ${heirarchyObj.name}`) as any
      )?.totalByClientOrderQuantity,
      totalByClientOrderValue: (storage.getItem(`${orderId} ${heirarchyObj.name}`) as any)
        ?.totalByClientOrderValue
    },
    orderTotalKpis: !index ? orderTotalKpis : {}
  }))
}

export default class ListDataService extends OrderDataService {
  httpService = new HttpService()

  // Hierarchy
  fetchHierarchy = (
    bsgId,
    orderId,
    orderType,
    filterData = [],
    searchKey = '',
    clientOrderId,
    includeKPIs,
    includeDoorKPIs,
    includeAWS = false,
    sortOptions?
  ) => {
    const orderPath =
      orderType === OrderType.Client
        ? `/clientOrderId/${orderId}`
        : `/clientOrderId/${clientOrderId}/doorOrderId/${orderId}`
    // const orderPath = `/${orderType ?? OrderType.Client}OrderId/${orderId}`
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_ORDER_PRODUCTS_HIERARCHY}/buyingSessionGroupId/${bsgId}${orderPath}`
    const accessToken: string = storage.getItem('access_token')
    const body = {
      filter: filterData,
      ...(sortOptions
        ? {
            sort: {
              sortBy: sortOptions.name.toUpperCase(),
              sortQuantityType: sortOptions.quantityType,
              sortOrder: sortOptions.sortOrder.toUpperCase()
            }
          }
        : {})
    }
    return this.httpService
      .post(url, body, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          ...(searchKey ? { searchKey } : {}),
          includeKPIs,
          includeDoorKPIs,
          includeAWS
        }
      })
      .then(response =>
        mapHierarchyToClientDraftQuantity(
          response.data?.data?.productHierarchy?.categories,
          response.data?.data?.orderTotalKpis
        )
      )
  }

  // Products
  fetchProductsByHierarchy = (
    bsgId: string,
    orderId: string,
    orderType: string,
    filterData = {},
    searchKey = '',
    includeTotalQty,
    includeSummary,
    sortOptions?
  ) => {
    const orderPath = `/${orderType ?? OrderType.Client}OrderId/${orderId}`
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_PRODUCTS}/buyingSessionGroupId/${bsgId}${orderPath}`
    const accessToken: string = storage.getItem('access_token')
    const body = {
      filter: filterData,
      ...(sortOptions
        ? {
            sort: {
              sortBy: sortOptions.name.toUpperCase(),
              sortQuantityType: sortOptions.quantityType,
              sortOrder: sortOptions.sortOrder.toUpperCase()
            }
          }
        : {})
    }

    return this.httpService
      .post(url, body, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          ...(searchKey ? { searchKey } : {}),
          includeTotalQty,
          includeSummary
        }
      })
      .then(response => mapProductClientDraftQuantity(response.data?.data?.products))
  }

  fetchFilterAttributes = (bsgId: string, orderId: string, orderType: string) => {
    const orderPath = `/${orderType ?? OrderType.Client}Order/${orderId}`
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_FILTER_ATTRIBUTES}/buyingSessionGroup/${bsgId}${orderPath}`
    const accessToken: string = storage.getItem('access_token')
    return this.httpService
      .get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })
      .then(response => response.data?.data?.filterAttributes)
  }

  updateProductDraftQuantity = async (
    bspId: string,
    orderId: string,
    payload: any,
    filterData = {},
    searchKey = '',
    includeAWS = false,
    rollUp = false
  ) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.PATCH_CLIENT_ORDER_QTY}/${orderId}/buyingSessionProduct/${bspId}`
      const accessToken: string = storage.getItem('access_token')
      const body = { ...payload, filter: filterData, rollUp: rollUp ? 'true' : 'false' }
      const response = await this.httpService.patch(url, body, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          ...(searchKey ? { searchKey } : {}),
          includeAWS
        }
      })

      return response?.data.data
    } catch (e) {
      console.log(e)
      // throw e
    }
  }
  rollUpAllQuantities = async (
    orderId: string,
    buyingSessionProductIds: string[],
    filterData = {},
    searchKey = ''
  ): Promise<any> => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.PATCH_CLIENT_ORDER_QTY}/${orderId}/rollUpDoorQuantityToProductQuantity`
      const accessToken: string = storage.getItem('access_token')
      const body = { buyingSessionProductIds, filter: filterData }
      const response = await this.httpService.patch(url, body, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          ...(searchKey ? { searchKey } : {})
        }
      })

      return response?.data?.data
    } catch (e) {
      console.log(e)
      // throw e
    }
  }

  updateDoorOrderQuantity = async (
    bspId: string,
    orderId: string,
    payload,
    kpisBy: string,
    filterData = {},
    searchKey = ''
  ) => {
    try {
      // const orderPath = `/${orderType ?? OrderType.Client}Order/${orderId}`
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.UPDATE_DOOR_ORDER_QUANTITY}/clientOrder/${orderId}/buyingSessionProduct/${bspId}`
      const body = { bulkUpdateProductQty: payload, filter: filterData }
      const accessToken: string = storage.getItem('access_token')

      const response = await this.httpService.patch(url, body, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          ...(searchKey ? { searchKey } : {}),
          kpisBy
        }
      })
      return response.data?.data
    } catch (e) {
      console.log(e)
      // throw e
    }
  }

  // *********** Quantity Sub Section ***********
  // **ToDo:** Improve from response?.data?.data
  bookAllDoors = async (bspId, clientOrderId) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_ORDER_PRODUCT_DETAILS}/clientOrder/${clientOrderId}/buyingSessionProduct/${bspId}`
      const accessToken: string = storage.getItem('access_token')

      const response = await this.httpService.post(url, null, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })
      return response?.data?.data
    } catch (e) {
      console.log(e)
      // throw e
    }
  }

  bookDoorOrder = async (clientOrderId, bspId, doorOrderIds) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.BOOK_UNBOOK_DOOR}/${clientOrderId}/buyingSessionProduct/${bspId}`
      const accessToken: string = storage.getItem('access_token')

      const response = await this.httpService.post(
        url,
        { doorOrderIds },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      )
      return response?.data?.data
    } catch (e) {
      console.log(e)
      // throw e
    }
  }

  // un-book one and un-book
  removeProductsFromOrder = async (clientOrderId, bspId, doorOrderIds) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.BOOK_UNBOOK_DOOR}/${clientOrderId}/buyingSessionProduct/${bspId}/remove`
      const accessToken: string = storage.getItem('access_token')
      const response = await this.httpService.post(
        url,
        {
          ...(doorOrderIds?.length
            ? {
                doorOrderIds
              }
            : {})
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      )
      return response?.data?.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  unbookDoors = async (
    clientOrderId,
    bspId,
    doorOrderIds,
    filterData = {},
    searchKey = ''
  ) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.BOOK_UNBOOK_DOOR}/${clientOrderId}/buyingSessionProduct/${bspId}/unbook`
      const accessToken: string = storage.getItem('access_token')
      const response = await this.httpService.post(
        url,
        {
          params: {
            ...(searchKey ? { searchKey } : {})
          },
          ...(doorOrderIds?.length
            ? {
                doorOrderIds,
                filter: filterData
              }
            : { filter: filterData })
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      )
      return response?.data?.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  unbookDoorForDoorOrder = async (
    doorOrderId,
    bspId,
    filterData = {},
    searchKey = ''
  ) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.UNBOOK_DOOR_FOR_DOOR_ORDER}/${doorOrderId}/buyingSessionProduct/${bspId}/unbook`
      const accessToken: string = storage.getItem('access_token')
      const response = await this.httpService.post(
        url,
        {
          params: {
            ...(searchKey ? { searchKey } : {})
          },
          filter: filterData
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      )
      return response?.data?.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  bulkUpdateProductQty = async (
    clientOrderId,
    bspId,
    payload,
    filterData = {},
    searchKey = ''
  ) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.BULK_UPDATE_PRODUCT_QUANTITY}/clientOrder/${clientOrderId}/buyingSessionProduct/${bspId}`
      const accessToken: string = storage.getItem('access_token')
      const body = { bulkUpdateProductQty: payload, filter: filterData }
      const response = await this.httpService.patch(url, body, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          ...(searchKey ? { searchKey } : {})
        }
      })
      return response?.data?.data
    } catch (e) {}
  }

  // update single size qty
  updateSizeQuantity = async (
    {
      clientOrderId,
      doorOrderId,
      bspId,
      sizeId
    }: { clientOrderId: string; doorOrderId: string; bspId: string; sizeId: string },
    payload: { quantity: number },
    filterData = {},
    searchKey = ''
  ) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.UPDATE_SIZE_QUANTITY}/clientOrder/${clientOrderId}/doorOrder/${doorOrderId}/buyingSessionProduct/${bspId}/size/${sizeId}`
      const body = { ...payload, filter: filterData }
      const accessToken: string = storage.getItem('access_token')

      const response = await this.httpService.patch(url, body, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        params: {
          ...(searchKey ? { searchKey } : {})
        }
      })
      return response.data?.data
    } catch (e) {
      console.log(e)
    }
  }
}
